import React, { useState, useEffect, useCallback } from 'react'
import { Search, MapPin, Globe, Plus, Check, AlertCircle } from 'lucide-react'
import merchantService from '@/services/merchantService'
import { configService, Region as GlobalRegion } from '@/services/configService'

interface GlobalRegionSelectorProps {
  onSelect: (regionId: string) => void
  onSwitchToCreateMode: () => void
  organizationId: string
  selectedRegionId?: string
  className?: string
}

const GlobalRegionSelector: React.FC<GlobalRegionSelectorProps> = ({
  onSelect,
  onSwitchToCreateMode,
  organizationId: _organizationId,
  selectedRegionId: _selectedRegionId = '',
  className = ''
}) => {
  const [regions, setRegions] = useState<GlobalRegion[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<string>('')
  const [countries, setCountries] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)

  // Load countries for filtering
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await configService.countries.getAll({ enabled: true })
        setCountries(response.data || [])
      } catch (err) {
        console.error('Error loading countries:', err)
      }
    }
    loadCountries()
  }, [])

  // Load regions with debounced search
  const loadRegions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const filters: any = {
        enabled: true,
        search: searchTerm || undefined,
        country_id: selectedCountry || undefined
      }
      
      const regionsList = await merchantService.getAvailableGlobalRegions(filters)
      setRegions(regionsList)
    } catch (err: any) {
      console.error('Error loading global regions:', err)
      setError('Erreur lors du chargement des régions')
      setRegions([])
    } finally {
      setLoading(false)
    }
  }, [searchTerm, selectedCountry])

  useEffect(() => {
    const timer = setTimeout(() => {
      loadRegions()
    }, 300) // Debounce search

    return () => clearTimeout(timer)
  }, [loadRegions])

  const handleRegionSelect = (regionId: string) => {
    onSelect(regionId)
  }

  const handleCreateNew = () => {
    onSwitchToCreateMode()
  }

  const getRegionTypeLabel = (typeCode?: string) => {
    if (!typeCode) return 'Région'
    
    switch (typeCode.toLowerCase()) {
      case 'state':
        return 'État'
      case 'province':
        return 'Province'
      case 'region':
        return 'Région'
      case 'department':
        return 'Département'
      case 'county':
        return 'Comté'
      default:
        return typeCode
    }
  }

  // Helper to get country info for a region
  const getCountryInfo = (region: GlobalRegion) => {
    const country = countries.find(c => c.id === region.country_id)
    return {
      name: country?.name || 'Inconnu',
      code: country?.code || '??',
      flag_emoji: country?.flag_emoji || ''
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search and filter controls */}
      <div className="space-y-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher une région par nom ou code..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filtrer par pays
          </label>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="">Tous les pays</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.flag_emoji} {country.name} ({country.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Regions list */}
      {!loading && regions.length > 0 && (
        <div className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => handleRegionSelect(region.id)}
              className="w-full text-left p-4 hover:bg-gray-50 focus:outline-none focus:bg-blue-50 transition-colors duration-150"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                    <h4 className="font-medium text-gray-900">{region.name}</h4>
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {region.code}
                    </span>
                  </div>
                  
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center text-sm text-gray-500">
                      <Globe className="h-3 w-3 mr-1" />
                      <span>
                        {getCountryInfo(region).name} ({getCountryInfo(region).code})
                        {getCountryInfo(region).flag_emoji && ` ${getCountryInfo(region).flag_emoji}`}
                      </span>
                    </div>
                    
                    {region.type_name && (
                      <div className="text-sm text-gray-500">
                        Type: {getRegionTypeLabel(region.type_code)} ({region.type_name})
                      </div>
                    )}
                    
                    {region.enabled === false && (
                      <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        Désactivée
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="ml-4">
                  <Check className="h-5 w-5 text-blue-600 opacity-0 group-hover:opacity-100" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && regions.length === 0 && searchTerm && (
        <div className="text-center py-8 border border-gray-200 rounded-md">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            Aucune région trouvée
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Aucune région ne correspond à votre recherche "{searchTerm}"
          </p>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer une nouvelle région "{searchTerm}"
          </button>
        </div>
      )}

      {/* Initial empty state (no search) */}
      {!loading && regions.length === 0 && !searchTerm && (
        <div className="text-center py-8 border border-gray-200 rounded-md">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            Aucune région disponible
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Aucune région globale n'est disponible pour le moment.
          </p>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer une nouvelle région
          </button>
        </div>
      )}

      {/* Create new region button (always visible) */}
      <div className="pt-4 border-t border-gray-200">
        <button
          onClick={handleCreateNew}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Créer une nouvelle région globale
        </button>
        <p className="mt-2 text-xs text-gray-500 text-center">
          Créez une nouvelle région globale qui pourra être utilisée par toutes les organisations
        </p>
      </div>
    </div>
  )
}

export default GlobalRegionSelector